<script setup>
import { ref, computed, onMounted } from 'vue';
import { useEquipmentsStore } from '@/store/equipments.js';
import { useAuthStore } from '@/store/auth.js';
import { useLocationStore } from '@/store/location.js';
import { debounce } from 'lodash';
import axios from 'axios';
import MapPickerModal from '@/components/MapPickerModal.vue';
import SearchResultsModal from '@/components/SearchResultsModal.vue';
import { useGeolocation } from '@/composables/useGeolocation.js';
import { useLocationSearch } from '@/composables/useLocationSearch.js';

const store        = useEquipmentsStore();
const authStore    = useAuthStore();
const locationStore = useLocationStore();
const apiBaseUrl   = import.meta.env.VITE_API_BASE_URL;
const { requestLocation } = useGeolocation();

// ── State ──────────────────────────────────────────────────────────────────
const locationInput   = ref('');
const locationInputEl = ref(null);
const isFetchingGPS   = ref(false);
const locationSaved   = ref(false);
const locationError   = ref('');
const showDropdown    = ref(false);
const showMap         = ref(false);
const activeCity      = ref('');
const showPermissionPrompt = ref(false); // in-app permission explanation dialog
const { suggestions: placeSuggestions, showSuggestions: showPlaceSuggestions, fetchSuggestions, resolvePlace, clearSuggestions } = useLocationSearch();

// ── Recent searches ────────────────────────────────────────────────────────
const RECENT_KEY = 'usenlease_recent_locations';
const MAX_RECENT = 5;
const recentLocations = ref(JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'));

const saveToRecent = ({ label, city }) => {
  const list = recentLocations.value.filter(r => r.city !== city);
  recentLocations.value = [{ label, city }, ...list].slice(0, MAX_RECENT);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recentLocations.value));
};

const removeRecent = (city) => {
  recentLocations.value = recentLocations.value.filter(r => r.city !== city);
  localStorage.setItem(RECENT_KEY, JSON.stringify(recentLocations.value));
};

// ── Dropdown ───────────────────────────────────────────────────────────────
const showingDropdown = computed(() =>
  showDropdown.value && recentLocations.value.length > 0
);
const onFocus = () => { showDropdown.value = true; };
const onBlur  = () => { setTimeout(() => { showDropdown.value = false; }, 200); };

// ── Apply location filter ──────────────────────────────────────────────────
const applyLocationFilter = async (label, city, state = '', country = '') => {
  locationInput.value  = label;
  activeCity.value     = city;
  showDropdown.value   = false;
  store.selectedCities = [city];
  store.selectedState  = state;
  store.selectedCountry = country;

  // Show results in modal
  showSearchModal.value = true;
  isSearching.value = true;
  try {
    await store.applyFilters();
    searchResults.value = [...store.equipments];
  } finally {
    isSearching.value = false;
  }

  saveToRecent({ label, city });
};

const clearLocation = () => {
  locationInput.value  = '';
  activeCity.value     = '';
  locationError.value  = '';
  store.selectedCities = [];
  store.selectedState  = '';
  store.selectedCountry = '';
  store.applyFilters();
};

// ── Save to backend ────────────────────────────────────────────────────────
const saveUserLocation = async (payload, coords, formatted) => {
  // Always hydrate the store so MapPicker can use the coords (even if not logged in)
  locationStore.hydrateUserFromAddressModel({
    ...payload, formatted_address: formatted,
    latitude: coords?.lat, longitude: coords?.lng,
  });

  if (!authStore.isAuthenticated) return;
  try {
    await axios.post(
      `${apiBaseUrl}/api/locations/addresses/user-address/`,
      { ...payload, formatted_address: formatted },
      { withCredentials: true }
    );
    locationSaved.value = true;
    setTimeout(() => (locationSaved.value = false), 2500);
  } catch { /* non-blocking */ }
};

// ── Google Places autocomplete ─────────────────────────────────────────────
const waitForGoogle = () =>
  new Promise(resolve => {
    const check = () => window.google?.maps?.places ? resolve() : setTimeout(check, 100);
    check();
  });

const onLocationInput = () => {
  fetchSuggestions(locationInput.value);
};

const selectSuggestion = async (suggestion) => {
  clearSuggestions();
  locationInput.value = suggestion.full;
  const result = await resolvePlace(suggestion.placeId, suggestion._prediction);
  if (!result) return;
  locationError.value = '';
  if (result.city) applyLocationFilter(result.formatted || suggestion.full, result.city, result.state, result.country);
  if (result.lat != null && result.lng != null) {
    saveUserLocation(
      { street_address: result.street_address, city: result.city,
        state: result.state, zip_code: result.zip_code, country: result.country },
      { lat: result.lat, lng: result.lng },
      result.formatted || suggestion.full
    );
  }
};

// ── GPS current location ───────────────────────────────────────────────────
const useCurrentLocation = async () => {
  isFetchingGPS.value   = true;
  locationError.value   = '';
  showDropdown.value    = false;

  const result = await requestLocation();

  if (result.error) {
    isFetchingGPS.value = false;
    if (result.denied) {
      // Show in-app instructions instead of just an error string
      showPermissionPrompt.value = true;
    } else {
      locationError.value = result.error;
    }
    return;
  }

  try {
    await waitForGoogle();
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat: result.coords.lat, lng: result.coords.lng } },
      async (results, status) => {
        if (status !== 'OK' || !results[0]) {
          locationError.value = 'Could not resolve your location. Try the map.';
          isFetchingGPS.value = false;
          return;
        }
        const r   = results[0];
        const get = (type) =>
          r.address_components.find(c => c.types.includes(type))?.long_name || '';
        const city = get('locality') || get('postal_town') ||
          get('administrative_area_level_2') || get('administrative_area_level_1');
        const state = get('administrative_area_level_1');
        const country = get('country');
        applyLocationFilter(r.formatted_address, city, state, country);
        await saveUserLocation(
          { street_address: [get('street_number'), get('route')].filter(Boolean).join(' '),
            city, state: get('administrative_area_level_1'),
            zip_code: get('postal_code'), country: get('country') },
          result.coords,
          r.formatted_address
        );
        isFetchingGPS.value = false;
      }
    );
  } catch {
    locationError.value = 'Could not resolve your location. Try the map.';
    isFetchingGPS.value = false;
  }
};

// ── Map modal confirm ──────────────────────────────────────────────────────
const onMapConfirm = async (result) => {
  applyLocationFilter(result.formatted, result.city, result.state, result.country);
  await saveUserLocation(
    { street_address: result.street_address, city: result.city,
      state: result.state, zip_code: result.zip_code, country: result.country },
    { lat: result.lat, lng: result.lng },
    result.formatted
  );
  showMap.value = false;
};

// ── Search ─────────────────────────────────────────────────────────────────
const showSearchModal = ref(false);
const searchResults = ref([]);
const isSearching = ref(false);

const performSearch = async () => {
  const query = store.searchQuery?.trim();
  if (!query) return;
  showSearchModal.value = true;
  isSearching.value = true;
  try {
    await store.applyFilters();
    searchResults.value = [...store.equipments];
  } finally {
    isSearching.value = false;
  }
};

const debouncedSearch = debounce(() => {
  const query = store.searchQuery?.trim();
  if (!query) {
    showSearchModal.value = false;
    searchResults.value = [];
    return;
  }
  performSearch();
}, 700);

const closeSearchModal = () => {
  showSearchModal.value = false;
};

// ── Init ───────────────────────────────────────────────────────────────────
onMounted(async () => {
  await store.fetchCategories();
  if (!store.hasCompletedEquipmentFetch) {
    await store.applyFilters();
  }
  if (locationStore.userAddress?.formattedAddress) {
    locationInput.value = locationStore.userAddress.formattedAddress;
    activeCity.value    = locationStore.userAddress?.city || '';
  }
});
</script>

<template>
  <!-- ═══ HERO ═══ Compact, fixed-height, no expansion on dropdown -->
  <section class="hero-section relative" style="z-index: 20;">
    <!-- Deep dark base (dark mode) / Light gradient (light mode) -->
    <div class="absolute inset-0 bg-theme-bg"></div>
    <!-- Very subtle warm wash at center top -->
    <div class="absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_-10%,rgba(230,141,0,0.08),transparent_60%)] dark:bg-[radial-gradient(ellipse_70%_50%_at_50%_-10%,rgba(230,141,0,0.10),transparent_60%)]"></div>

    <!-- Decorative SVG curves -->
    <div class="absolute inset-0 pointer-events-none overflow-hidden">
      <!-- Sweeping wave — bottom left -->
      <svg class="absolute bottom-0 left-0 w-full h-24 opacity-[0.03] dark:opacity-[0.06]" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M0 80C240 20 480 100 720 60C960 20 1200 90 1440 50V120H0Z" class="fill-theme-text"/>
      </svg>
      <!-- Flowing arc — top right -->
      <svg class="absolute -top-6 -right-10 w-[50%] h-full opacity-[0.02] dark:opacity-[0.04]" viewBox="0 0 600 300" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M600 0C450 80 350 200 200 180C80 164 20 80 0 0" class="stroke-theme-text" stroke-width="1.5" fill="none"/>
        <path d="M600 30C430 120 330 220 180 200C60 184 10 110 0 40" class="stroke-theme-text" stroke-width="0.8" fill="none"/>
      </svg>
      <!-- Flowing arc — left side -->
      <svg class="absolute -top-4 -left-10 w-[45%] h-full opacity-[0.02] dark:opacity-[0.04]" viewBox="0 0 500 300" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 20C100 100 200 160 350 130C450 108 490 50 500 0" class="stroke-theme-text" stroke-width="1.5" fill="none"/>
        <path d="M0 50C80 140 190 190 340 155C440 130 485 80 500 30" class="stroke-theme-text" stroke-width="0.8" fill="none"/>
      </svg>

      <!-- Dot grid — left edge -->
      <svg class="absolute left-0 top-0 h-full w-32 opacity-[0.02] dark:opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
        <defs><pattern id="hero-dots-l" x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1" class="fill-theme-text"/></pattern></defs>
        <rect width="100%" height="100%" fill="url(#hero-dots-l)"/>
      </svg>
      <!-- Dot grid — right edge -->
      <svg class="absolute right-0 top-0 h-full w-32 opacity-[0.02] dark:opacity-[0.04]" xmlns="http://www.w3.org/2000/svg">
        <defs><pattern id="hero-dots-r" x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse"><circle cx="1.5" cy="1.5" r="1" class="fill-theme-text"/></pattern></defs>
        <rect width="100%" height="100%" fill="url(#hero-dots-r)"/>
      </svg>

      <!-- Animated floating PrimeIcons — rent/lease themed -->
      <!-- Left zone -->
      <i class="pi pi-car hero-float hero-float-1 absolute left-[4%] top-[14%] opacity-[0.07] text-2xl"></i>
      <i class="pi pi-wrench hero-float hero-float-2 absolute left-[10%] bottom-[18%] opacity-[0.06] text-lg"></i>
      <i class="pi pi-map-marker hero-float hero-float-3 absolute left-[3%] bottom-[45%] opacity-[0.06] text-xl"></i>
      <i class="pi pi-key hero-float hero-float-4 absolute left-[15%] top-[60%] opacity-[0.05] text-base"></i>
      <i class="pi pi-hammer hero-float hero-float-5 absolute left-[8%] top-[40%] opacity-[0.05] text-lg"></i>
      <i class="pi pi-warehouse hero-float hero-float-6 absolute left-[18%] bottom-[10%] opacity-[0.04] text-xl"></i>

      <!-- Right zone -->
      <i class="pi pi-home hero-float hero-float-7 absolute right-[5%] top-[20%] opacity-[0.07] text-xl"></i>
      <i class="pi pi-truck hero-float hero-float-8 absolute right-[9%] bottom-[22%] opacity-[0.06] text-2xl"></i>
      <i class="pi pi-shopping-bag hero-float hero-float-1 absolute right-[4%] top-[55%] opacity-[0.06] text-lg"></i>
      <i class="pi pi-credit-card hero-float hero-float-3 absolute right-[16%] top-[12%] opacity-[0.05] text-base"></i>
      <i class="pi pi-shield hero-float hero-float-5 absolute right-[12%] bottom-[40%] opacity-[0.05] text-lg"></i>
      <i class="pi pi-cog hero-float hero-float-2 absolute right-[18%] bottom-[8%] opacity-[0.04] text-xl"></i>

      <!-- Upper scatter -->
      <i class="pi pi-box hero-float hero-float-4 absolute left-[28%] top-[8%] opacity-[0.04] text-lg"></i>
      <i class="pi pi-camera hero-float hero-float-6 absolute right-[28%] top-[6%] opacity-[0.04] text-xl"></i>
      <i class="pi pi-building hero-float hero-float-8 absolute left-[40%] top-[5%] opacity-[0.03] text-base"></i>

      <!-- Lower scatter -->
      <i class="pi pi-tag hero-float hero-float-7 absolute left-[32%] bottom-[10%] opacity-[0.04] text-base"></i>
      <i class="pi pi-wallet hero-float hero-float-1 absolute right-[35%] bottom-[12%] opacity-[0.04] text-lg"></i>
      <i class="pi pi-calendar hero-float hero-float-3 absolute left-[50%] bottom-[6%] opacity-[0.03] text-base"></i>
      <i class="pi pi-star hero-float hero-float-5 absolute right-[42%] top-[15%] opacity-[0.03] text-sm"></i>
      <i class="pi pi-globe hero-float hero-float-2 absolute left-[22%] top-[30%] opacity-[0.03] text-lg"></i>
      <i class="pi pi-bolt hero-float hero-float-6 absolute right-[22%] bottom-[30%] opacity-[0.03] text-base"></i>
    </div>

    <!-- Bottom edge — smooth curve into page -->
    <div class="absolute bottom-0 left-0 right-0">
      <svg class="w-full h-3" viewBox="0 0 1440 12" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
        <path d="M0 12C360 0 1080 0 1440 12V12H0Z" fill="var(--color-bg, #ffffff)"/>
      </svg>
    </div>

    <div class="relative z-10 container mx-auto px-4 py-3 sm:py-4">
      <!-- Top row: Heading + Tagline  -->
      <div class="text-center mb-3">
        <h1 class="text-xl sm:text-2xl lg:text-3xl font-extrabold text-primary-600 dark:text-theme-text tracking-tight leading-tight">
          Rent or Lease Anything&nbsp;You&nbsp;Need
        </h1>
        <p class="mt-0.5 text-xs sm:text-sm text-neutral-600 dark:text-theme-text2 max-w-xl mx-auto">
          Find the perfect item nearby. Search by location or keyword.
        </p>
      </div>

      <!-- Search bar — single row on sm+, stacked on mobile -->
<div class="relative max-w-2xl mx-auto">
  <div class="bg-transparent sm:bg-theme-card sm:rounded-2xl sm:shadow-theme-modal sm:border sm:border-theme-border overflow-visible">
    <div class="flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-0 sm:p-1.5">

      <!-- Location input -->
      <div
        class="relative flex items-center flex-1 min-w-0
               px-3 py-2.5 rounded-xl
               bg-white dark:bg-theme-card
               border border-neutral-200 dark:border-theme-border
               shadow-sm
               sm:bg-theme-bg2/50 sm:dark:bg-theme-card/50 sm:border-0 sm:shadow-none sm:rounded-xl sm:px-3 sm:py-2
        ">
        <i class="pi pi-map-marker text-primary-600 text-base sm:text-lg shrink-0 mr-2"></i>

        <input
          ref="locationInputEl"
          v-model="locationInput"
          type="text"
          placeholder="City or address…"
          autocomplete="off"
          class="w-full bg-transparent text-theme-text text-sm placeholder:text-neutral-400 dark:placeholder:text-theme-muted
                 focus:outline-none py-0.5"
          @focus="onFocus"
          @blur="onBlur"
          @input="onLocationInput"
        />

        <span v-if="locationSaved" class="text-green-500 shrink-0 mr-1">
          <i class="pi pi-check-circle text-sm"></i>
        </span>

        <button
          v-if="locationInput && !locationSaved"
          @click="clearLocation"
          class="text-neutral-400 hover:text-theme-text shrink-0 mr-0.5 p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-theme-bg3 transition"
          title="Clear location"
        >
          <i class="pi pi-times text-xs"></i>
        </button>

        <button
          @click="useCurrentLocation"
          :disabled="isFetchingGPS"
          class="shrink-0 p-1.5 text-primary-600 hover:text-primary-400 rounded-full hover:bg-primary-50 dark:hover:bg-primary-600/10 transition disabled:opacity-50"
          title="Use my current location"
        >
          <svg v-if="!isFetchingGPS" class="w-4.5 h-4.5 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3"></path>
            <circle cx="12" cy="12" r="8" stroke-dasharray="2 2"></circle>
          </svg>
          <i v-else class="pi pi-spin pi-spinner text-sm"></i>
        </button>

        <button
          @click="showMap = true"
          class="shrink-0 p-1.5 text-primary-600 hover:text-primary-400 rounded-full hover:bg-primary-50 dark:hover:bg-primary-600/10 transition"
          title="Pick on map"
        >
          <svg class="w-4.5 h-4.5 sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
        </button>
      </div>

      <!-- Divider (desktop only) -->
      <div class="hidden sm:flex items-center px-0.5">
        <div class="w-px h-7 bg-neutral-200 dark:bg-theme-border"></div>
      </div>

      <!-- Keyword search input -->
      <div
        class="flex items-center flex-1 min-w-0
               px-3 py-2.5 rounded-xl
               bg-white dark:bg-theme-card
               border border-neutral-200 dark:border-theme-border
               shadow-sm
               sm:bg-theme-bg2/50 sm:dark:bg-theme-card/50 sm:border-0 sm:shadow-none sm:rounded-xl sm:px-3 sm:py-2
      ">
        <i class="pi pi-search text-neutral-400 dark:text-theme-muted text-base shrink-0 mr-2"></i>

        <input
          type="text"
          v-model="store.searchQuery"
          @input="debouncedSearch"
          @keydown.enter.prevent="performSearch"
          placeholder="What are you looking for?"
          class="w-full bg-transparent text-theme-text text-sm placeholder:text-neutral-400 dark:placeholder:text-theme-muted focus:outline-none py-0.5"
        />

        <!-- Desktop button -->
        <button
          @click="performSearch"
          class="hidden sm:flex shrink-0 ml-2 items-center gap-1.5 bg-primary-600 hover:bg-primary-500 text-white px-5 py-2 rounded-xl text-sm font-semibold transition active:scale-95 shadow-sm"
        >
          <i class="pi pi-search text-xs"></i> Search
        </button>
      </div>
    </div>

    <!-- Mobile button -->
    <div class="sm:hidden pt-1">
      <button
        @click="performSearch"
        class="w-full bg-primary-600 hover:bg-primary-500 text-white py-2.5 rounded-xl text-sm font-semibold transition active:scale-95 flex items-center justify-center gap-1.5 shadow-sm"
      >
        <i class="pi pi-search text-xs"></i> Search
      </button>
    </div>
  </div>


          <!-- Active filter badge — inside the card, compact -->
          <div v-if="activeCity" class="px-3 pb-2 pt-0">
            <span class="inline-flex items-center gap-1 bg-primary-50 dark:bg-primary-600/15 text-primary-700 dark:text-primary-400 text-xs px-2.5 py-1 rounded-full font-medium">
              <i class="pi pi-map-marker text-[10px]"></i>
              {{ activeCity }}
              <button @click="clearLocation" class="ml-1 hover:text-red-500 transition">
                <i class="pi pi-times text-[10px]"></i>
              </button>
            </span>
          </div>

          <!-- Recent searches dropdown — absolutely positioned so it doesn't push hero height -->
          <transition name="slide-down">
            <div v-if="showingDropdown"
              class="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-theme-card rounded-xl shadow-lg dark:shadow-theme-modal border border-neutral-200 dark:border-theme-border z-50 max-h-64 overflow-y-auto">
              <p class="text-base font-semibold text-neutral-500 dark:text-theme-muted uppercase tracking-wider px-4 pt-3 pb-1">Recent searches</p>
              <ul class="px-2">
                <li v-for="item in recentLocations" :key="item.city"
                  class="flex items-center justify-between group hover:bg-neutral-100 dark:hover:bg-theme-bg2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors"
                  @mousedown.prevent="applyLocationFilter(item.label, item.city)">
                  <div class="flex items-center gap-2.5 min-w-0">
                    <i class="pi pi-history text-neutral-400 dark:text-theme-muted text-base shrink-0"></i>
                    <span class="text-base text-neutral-800 dark:text-theme-text truncate">{{ item.label }}</span>
                  </div>
                  <button @mousedown.prevent.stop @click.stop="removeRecent(item.city)"
                    class="text-neutral-400 dark:text-theme-muted hover:text-red-500 dark:hover:text-red-500 transition-colors shrink-0 ml-2 p-1.5 rounded-full hover:bg-red-50 dark:hover:bg-red-500/10"
                    title="Remove">
                    <i class="pi pi-times text-lg"></i>
                  </button>
                </li>
              </ul>
              <div class="border-t border-neutral-200 dark:border-theme-border mx-2 mt-1 pt-1 pb-2 space-y-0.5">
                <div class="flex items-center gap-2.5 hover:bg-neutral-100 dark:hover:bg-theme-bg2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors"
                  @mousedown.prevent="useCurrentLocation">
                  <svg class="w-5 h-5 text-primary-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 2v3M12 19v3M2 12h3M19 12h3"/>
                    <circle cx="12" cy="12" r="8" stroke-dasharray="2 2"/>
                  </svg>
                  <span class="text-sm text-primary-600 font-medium">Use my current location</span>
                  <i v-if="isFetchingGPS" class="pi pi-spin pi-spinner text-xs text-neutral-400 dark:text-theme-muted ml-auto"></i>
                </div>
                <div class="flex items-center gap-2.5 hover:bg-neutral-100 dark:hover:bg-theme-bg2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors"
                  @mousedown.prevent="showMap = true">
                  <svg class="w-5 h-5 text-primary-600 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  <span class="text-sm text-primary-600 font-medium">Pick on map</span>
                </div>
              </div>
            </div>
          </transition>

          <!-- Places autocomplete suggestions -->
          <transition name="slide-down">
            <div v-if="showPlaceSuggestions && placeSuggestions.length"
              class="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-theme-card rounded-xl shadow-lg dark:shadow-theme-modal border border-neutral-200 dark:border-theme-border z-50 max-h-64 overflow-y-auto">
              <ul class="px-2 py-1">
                <li v-for="s in placeSuggestions" :key="s.placeId"
                  class="flex items-center gap-2.5 hover:bg-neutral-100 dark:hover:bg-theme-bg2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors"
                  @mousedown.prevent="selectSuggestion(s)">
                  <i class="pi pi-map-marker text-neutral-400 dark:text-theme-muted text-base shrink-0"></i>
                  <div class="min-w-0">
                    <span class="text-sm text-neutral-800 dark:text-theme-text block truncate">{{ s.main }}</span>
                    <span v-if="s.secondary" class="text-xs text-neutral-500 dark:text-theme-muted block truncate">{{ s.secondary }}</span>
                  </div>
                </li>
              </ul>
            </div>
          </transition>
        </div>

        <!-- Error / hint — below card, compact -->
        <div class="mt-1.5 min-h-[18px] px-1 text-center">
          <p v-if="locationError" class="text-neutral-800 dark:text-theme-text text-xs flex items-center justify-center gap-1">
            <i class="pi pi-exclamation-circle"></i> {{ locationError }}
          </p>
          <p v-else-if="!authStore.isAuthenticated && activeCity" class="text-neutral-500 dark:text-theme-text2 text-xs">
            <i class="pi pi-info-circle"></i> Log in to save your location
          </p>
        </div>
    </div>
  </section>

  <!-- Permission denied dialog -->
  <teleport to="body">
    <div
      v-if="showPermissionPrompt"
      class="fixed inset-0 flex items-center justify-center bg-black/60 p-4"
      style="z-index: 99998;"
      @click.self="showPermissionPrompt = false"
    >
      <div class="bg-theme-card rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col items-center text-center gap-4">
        <div class="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center">
          <i class="pi pi-map-marker text-3xl text-primary-600"></i>
        </div>
        <div>
          <h3 class="text-base font-bold text-theme-text mb-1">Location access needed</h3>
          <p class="text-sm text-theme-muted leading-relaxed">
            Usenlease needs your location to show nearby equipment.
            Your browser has blocked location access.
          </p>
        </div>
        <div class="bg-theme-bg2 rounded-xl p-3 text-left w-full">
          <p class="text-xs font-semibold text-theme-text2 mb-1">How to enable:</p>
          <ol class="text-xs text-theme-muted space-y-1 list-decimal list-inside">
            <li>Click the <strong>lock icon</strong> in your browser's address bar</li>
            <li>Find <strong>Location</strong> and set it to <strong>Allow</strong></li>
            <li>Refresh the page and try again</li>
          </ol>
        </div>
        <div class="flex gap-3 w-full">
          <button
            @click="showPermissionPrompt = false"
            class="flex-1 py-2.5 rounded-xl border border-theme-border text-theme-text2 text-sm font-medium hover:bg-theme-bg2 transition"
          >
            Cancel
          </button>
          <button
            @click="showPermissionPrompt = false; showMap = true"
            class="flex-1 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-400 transition"
          >
            Use map instead
          </button>
        </div>
      </div>
    </div>
  </teleport>

  <!-- Search results modal -->
  <SearchResultsModal
    :show="showSearchModal"
    :results="searchResults"
    :isLoading="isSearching"
    @close="closeSearchModal"
  />

  <!-- Map modal -->
  <MapPickerModal
    :show="showMap"
    :initial-center="locationStore.userCoords"
    :initial-address="locationInput"
    @close="showMap = false"
    @confirm="onMapConfirm"
  />
</template>

<style scoped>
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.15s ease; }
.slide-down-enter-from, .slide-down-leave-to { opacity: 0; transform: translateY(-4px); }

/* Google Places autocomplete dropdown — must sit above everything */
:global(.pac-container) {
  z-index: 99999 !important;
}

/* ── Floating icon animations — 8 unique drifts ── */
.hero-float {
  will-change: transform;
  color: var(--color-text);
}
.hero-float-1 { animation: float1 7s ease-in-out infinite; }
.hero-float-2 { animation: float2 9s ease-in-out infinite; }
.hero-float-3 { animation: float3 8s ease-in-out infinite 0.5s; }
.hero-float-4 { animation: float4 10s ease-in-out infinite 1s; }
.hero-float-5 { animation: float5 7.5s ease-in-out infinite 0.3s; }
.hero-float-6 { animation: float6 8.5s ease-in-out infinite 1.2s; }
.hero-float-7 { animation: float7 9.5s ease-in-out infinite 0.7s; }
.hero-float-8 { animation: float8 11s ease-in-out infinite 0.4s; }

@keyframes float1 {
  0%, 100% { transform: translate(0, 0) rotate(-8deg); }
  50%      { transform: translate(6px, -10px) rotate(4deg); }
}
@keyframes float2 {
  0%, 100% { transform: translate(0, 0) rotate(5deg); }
  50%      { transform: translate(-8px, 8px) rotate(-6deg); }
}
@keyframes float3 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  33%      { transform: translate(5px, -6px) rotate(8deg); }
  66%      { transform: translate(-4px, 4px) rotate(-4deg); }
}
@keyframes float4 {
  0%, 100% { transform: translate(0, 0) rotate(10deg); }
  50%      { transform: translate(-6px, -8px) rotate(-3deg); }
}
@keyframes float5 {
  0%, 100% { transform: translate(0, 0) rotate(-5deg); }
  40%      { transform: translate(8px, 5px) rotate(6deg); }
  70%      { transform: translate(-3px, -7px) rotate(-2deg); }
}
@keyframes float6 {
  0%, 100% { transform: translate(0, 0) rotate(3deg); }
  50%      { transform: translate(4px, -12px) rotate(-7deg); }
}
@keyframes float7 {
  0%, 100% { transform: translate(0, 0) rotate(-3deg); }
  35%      { transform: translate(-7px, 6px) rotate(5deg); }
  65%      { transform: translate(5px, -4px) rotate(-8deg); }
}
@keyframes float8 {
  0%, 100% { transform: translate(0, 0) rotate(7deg); }
  50%      { transform: translate(-5px, -9px) rotate(-5deg); }
}
</style>
